import { useEffect, useState } from "react";
import { collection, documentId, getDocs, query, where } from "firebase/firestore";
import { auth, db } from "../firebase";

// Foto de perfil actual de cada autor, leída de users/{uid}. Las publicaciones
// guardan una copia de la foto al publicarse (campo image) que queda rota si el
// autor cambia de foto; usar `photos[uid] || post.image`.
// Cache compartido entre pantallas: cada uid se pide una sola vez por sesión.
const cache = {};
const inFlight = {};

const fetchPhotos = async (uids) => {
  for (let i = 0; i < uids.length; i += 30) {
    const chunk = uids.slice(i, i + 30);
    const request = getDocs(query(collection(db, "users"), where(documentId(), "in", chunk)))
      .then((snap) => {
        // Los uids sin doc quedan en "" para no volver a pedirlos.
        chunk.forEach((uid) => { cache[uid] = ""; });
        snap.forEach((docSnap) => { cache[docSnap.id] = docSnap.data()?.profileImageUrl || ""; });
      })
      // Sin sesión las reglas no dejan leer users: se usa la foto de la publicación.
      .catch(() => {})
      .finally(() => { chunk.forEach((uid) => { delete inFlight[uid]; }); });
    chunk.forEach((uid) => { inFlight[uid] = request; });
    await request;
  }
};

const useAuthorPhotos = (uids) => {
  const key = [...new Set((uids || []).filter(Boolean))].sort().join(",");
  const [photos, setPhotos] = useState(() => ({ ...cache }));
  // Se reintenta al iniciar sesión: antes de que Firebase restaure la sesión la lectura falla.
  const [authUid, setAuthUid] = useState(auth.currentUser?.uid || null);

  useEffect(() => auth.onAuthStateChanged((u) => setAuthUid(u?.uid || null)), []);

  useEffect(() => {
    if (!key) return;
    const all = key.split(",");
    const missing = all.filter((uid) => !(uid in cache) && !inFlight[uid]);
    let active = true;
    Promise.all([
      fetchPhotos(missing),
      ...all.map((uid) => inFlight[uid]).filter(Boolean),
    ]).then(() => {
      if (active) setPhotos({ ...cache });
    });
    return () => { active = false; };
  }, [key, authUid]);

  return photos;
};

export default useAuthorPhotos;
